import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import { GetAgencyBranchesListParams } from '@nxsys/store/slices/agency/types';
import { Icons } from '@nxsys/types/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSVLink } from 'react-csv';
import { showMessage } from '@nxsys/utils/message';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import { useDeleteAgencySiteById } from '@nxsys/hooks/api/agency/useAgencyApi';
import AddBranchModal from '../create/AddBranchModal';
import DetailBranchModal from '../detail/DetailBranchModal';

const Branches = ({
  data,
  hasBorder = true,
}: {
  data: IAgency;
  hasBorder?: boolean;
}) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { loading, list, pagination, queryParams } = useSelector(
    (state: AppState) => state.agencyStore.agencyBranchList,
  );

  const [searchValue, setSearchValue] = useState('');
  const [isOpenAddNewBranch, setIsOpenAddNewBranch] = useState(false);
  const toggleAddNewBranch = useCallback(() => {
    setIsOpenAddNewBranch(!isOpenAddNewBranch);
  }, [isOpenAddNewBranch]);
  const [openDetailBranchModal, setOpenDetailBranchModal] = useState<{
    data: IBranch | null;
    open: boolean;
  }>({
    data: null,
    open: false,
  });
  const toggleDetailBranch = useCallback(() => {
    setOpenDetailBranchModal({
      ...openDetailBranchModal,
      open: !openDetailBranchModal.open,
    });
  }, [openDetailBranchModal.open]);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetAgencyBranchesListParams) => {
    const newParams = {
      ...params,
    };
    dispatch(
      agencyAction.getAgencyBranchesById({
        id: data.id,
        params: newParams,
      }),
    );
  };

  const refetchData = useCallback(() => {
    handleFetchData({ ...queryParams });
  }, [queryParams]);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedFetchData(value);
  };

  const handleViewDetail = useCallback((record: IBranch) => {
    handleOpenDetailBranch(record);
  }, []);

  const handleAddNew = useCallback(() => {
    setIsOpenAddNewBranch(true);
  }, []);

  const { run: deleteAgencySiteById } = useDeleteAgencySiteById();

  const handleConfirmDelete = (record: IBranch) => {
    AppConfirmationDelete.open({
      title: messages['common.delete'],
      paragraph: messages['common.deleteConfirm'],
      onConfirm: async () => {
        await deleteAgencySiteById(String(record.id));
        showMessage({
          type: 'success',
          content: messages['common.deleteSuccess'] as string,
        });
        refetchData();
      },
    });
  };

  const columns: ColumnsType<IBranch> = [
    {
      key: 'action',
      title: 'Action',
      dataIndex: '',
      align: 'left',
      width: 100,
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleViewDetail(record)}>
            <Tooltip title={messages['common.view']}>
              <span>
                <Icons.IconEdit />
              </span>
            </Tooltip>
          </li>
          <li>
            {/* @ts-ignore: CSVLink JSX type mismatch on CI */}
            <CSVLink
              data={[{ ...record, VATCode: record?.VATCode?.VATCode }]}
              filename={`agency-site-${record?.siteName}.csv`}
            >
              <Tooltip title={messages['common.download']}>
                <span>
                  <Icons.IconDownload />
                </span>
              </Tooltip>
            </CSVLink>
          </li>
          <li onClick={() => handleConfirmDelete(record)}>
            <Tooltip title={messages['common.delete']}>
              <span>
                <Icons.IconDelete />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
    },

    {
      key: 'id',
      title: messages['common.siteId'],
      dataIndex: 'id',
    },
    {
      key: 'siteName',
      title: messages['common.siteName'],
      dataIndex: 'siteName',
    },
    {
      key: 'code',
      title: messages['common.code'],
      dataIndex: 'code',
    },
    {
      key: 'VATCode',
      title: messages['common.vatCode'],
      dataIndex: 'VATCode',
      render: (props) => <span>{props?.VATCode || ''}</span>,
    },
    {
      key: 'siteManager',
      title: messages['common.siteManager'],
      dataIndex: 'siteManager',
      render: (props) => <span>{props?.name || '-'}</span>,
    },
  ];

  const handleOpenDetailBranch = useCallback((data: IBranch) => {
    setOpenDetailBranchModal({
      data,
      open: true,
    });
  }, []);

  return (
    <AppCard
      title={hasBorder ? messages['common.branches'] : undefined}
      hasBorder={hasBorder}
    >
      <AppsHeader>
        <div className="flex justify-between gap-x-2 w-full">
          <AppInputSearch value={searchValue} onChange={handleSearchChange} />
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={handleAddNew}
          />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current ?? 0}
          total={pagination.totalDocs ?? 0}
          pageSize={pagination.pageSize ?? 20}
          scroll={{ x: 1200, y: 700 }}
          titleEmpty={messages['branch.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          noDataType={!!searchValue ? 'noDataFound' : 'noDataCreated'}
          addNewBtnOnClick={handleAddNew}
          dataTypeName={messages['common.branches']}
        />
      </AppsContent>
      <AddBranchModal
        isOpen={isOpenAddNewBranch}
        toggle={toggleAddNewBranch}
        agencyData={data}
        refetchData={refetchData}
      />
      {openDetailBranchModal.open && (
        <DetailBranchModal
          isOpen={openDetailBranchModal.open}
          toggle={toggleDetailBranch}
          agencyData={data}
          refetchData={refetchData}
          data={openDetailBranchModal.data}
        />
      )}
    </AppCard>
  );
};

export default Branches;
